import { useEffect, useRef, useState } from "react";
import { Palette, RotateCcw, Check } from "lucide-react";
import { useStudentTheme } from "../context/StudentThemeContext";

/**
 * Lets the student pick their own sidebar/button accent color — a palette
 * icon in the header opens a small popover of preset swatches plus a
 * native color input for a fully custom choice. Nothing here is sent to
 * the backend; it's a per-browser preference (see StudentThemeContext),
 * and the default stays untouched for everyone until they open this.
 */
const ThemeColorPicker = () => {
  const { accentColor, isCustomAccent, presets, setAccentColor, resetAccentColor } = useStudentTheme();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="sp-theme-picker" ref={panelRef}>
      <button
        type="button"
        className="sp-header-icon-btn"
        onClick={() => setIsOpen((prev) => !prev)}
        title="Choose a sidebar/button color"
      >
        <Palette size={18} />
      </button>

      {isOpen && (
        <div className="sp-theme-picker-panel">
          <div className="sp-theme-picker-title">Accent color</div>
          <div className="sp-theme-picker-swatches">
            {presets.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className="sp-theme-picker-swatch"
                style={{ background: preset.value }}
                title={preset.label}
                onClick={() => setAccentColor(preset.value)}
              >
                {accentColor.toLowerCase() === preset.value.toLowerCase() && <Check size={14} color="#fff" />}
              </button>
            ))}
            <label className="sp-theme-picker-swatch sp-theme-picker-swatch--custom" title="Custom color">
              <input
                type="color"
                value={accentColor}
                onChange={(event) => setAccentColor(event.target.value)}
              />
            </label>
          </div>
          <button
            type="button"
            className="sp-theme-picker-reset"
            onClick={resetAccentColor}
            disabled={!isCustomAccent}
          >
            <RotateCcw size={13} /> Reset to default
          </button>
        </div>
      )}
    </div>
  );
};

export default ThemeColorPicker;
